import { useEffect, useState } from "react";
import { photoSignedUrl, photoResponsive } from "@/lib/photos";

/**
 * Small presentational tile for a single child_photos row.
 *
 * Bandwidth pass: requests a server-transformed 480px @ q=70 thumbnail
 * with a srcSet for retina displays (320 / 480 / 640). The original
 * bytes are reserved for the lightbox view. If the project's image
 * transformations aren't enabled, the helper falls back to the un-
 * transformed signed URL on its own; we additionally wire `onError`
 * to re-resolve to the original as a final safety net.
 */
export default function PhotoTile({ photo, onOpen, caption, "data-testid": dataTestid }) {
  const [imgProps, setImgProps] = useState(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let alive = true;
    setErrored(false);
    (async () => {
      try {
        const r = await photoResponsive(photo.storage_path, {
          widths: [320, 480, 640],
          quality: 70,
          resize: "cover",
          sizes: "(min-width: 768px) 25vw, 50vw",
        });
        if (alive) setImgProps(r);
      } catch {
        /* ignore — tile will show skeleton */
      }
    })();
    return () => { alive = false; };
  }, [photo.storage_path]);

  // Final safety net: if a transformed URL fails to render (e.g. transforms
  // disabled mid-flight), fall back to the original signed URL.
  async function onImageError() {
    if (errored) return;
    setErrored(true);
    try {
      const original = await photoSignedUrl(photo.storage_path);
      if (original) setImgProps({ src: original, srcSet: undefined, sizes: undefined });
    } catch {
      /* leave skeleton */
    }
  }

  return (
    <button
      type="button"
      data-testid={dataTestid}
      onClick={() => onOpen?.(photo)}
      className="group relative block aspect-square w-full overflow-hidden rounded-2xl bg-neutral-100 transition-all hover:opacity-95 active:scale-[0.99]"
    >
      {imgProps ? (
        <img
          src={imgProps.src}
          srcSet={imgProps.srcSet}
          sizes={imgProps.sizes}
          alt={photo.caption || "Child photo"}
          loading="lazy"
          decoding="async"
          onError={onImageError}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
      ) : (
        <div className="h-full w-full animate-pulse bg-neutral-200" />
      )}
      {caption && photo.caption && (
        <p className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent p-2 text-left text-[11px] font-medium text-white">
          {photo.caption}
        </p>
      )}
    </button>
  );
}
